import { useEffect, useMemo } from 'react';
import { RouteObject, createRoutesFromChildren, matchRoutes, useLocation } from 'react-router-dom';

import { appRouteElements } from '../api/AppRoutes';
import { startClickTracking } from '../analytics/clickTracker';
import { PageInfo, setPageResolver, startTracking, trackPageView } from '../analytics/tracker';

/** Route parameters that name the record a page is about, and the record's type. */
const SUBJECT_TYPES: Record<string, string> = {
  caseId: 'Case',
  clientId: 'Client',
  defendantId: 'Defendant',
  deductionId: 'Deduction',
  settlementId: 'Settlement',
  documentId: 'DocumentRequest',
  signatureId: 'SignatureRequest',
  dataId: 'DataRequest',
  lawyerId: 'FirmUser',
  accountId: 'VendorAccount',
  batchCode: 'AutoSweepBatch',
};

/** Redirects (e.g. /cases/1 to /cases/1/home) settle within this time; only the final page counts. */
const SETTLE_MS = 300;

/**
 * A path as the app reports it: the route template (e.g. /cases/:caseId/home, never the real
 * URL) and the record the page is about (the innermost record: nested routes come last).
 */
const resolvePage = (routes: RouteObject[], pathname: string): PageInfo => {
  const matches = matchRoutes(routes, pathname) ?? [];
  const template = matches
    .map((match) => match.route.path)
    .filter((path): path is string => Boolean(path))
    .join('/');
  const params = matches.length > 0 ? matches[matches.length - 1].params : {};

  let subjectType: string | undefined;
  let subjectId: string | undefined;
  Object.entries(params).forEach(([name, value]) => {
    if (value && SUBJECT_TYPES[name]) {
      subjectType = SUBJECT_TYPES[name];
      subjectId = value;
    }
  });

  return { page: `/${template}`, subjectType, subjectId };
};

/**
 * Usage analytics of the web app: a page view on every route change (query strings such as
 * filters and paging are not page changes), and clicks on elements tagged for tracking
 * (see analytics/clickTracker.ts). Explicit events are recorded with trackEvent.
 */
export const useAnalyticsTracking = (): void => {
  const { pathname } = useLocation();
  const routes = useMemo(() => createRoutesFromChildren(appRouteElements), []);

  useEffect(() => {
    setPageResolver((path) => resolvePage(routes, path));
    const stopTracking = startTracking();
    const stopClickTracking = startClickTracking();
    return () => {
      stopClickTracking();
      stopTracking();
    };
  }, [routes]);

  useEffect(() => {
    const timer = window.setTimeout(() => trackPageView(resolvePage(routes, pathname)), SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [pathname, routes]);
};
